export interface DatasetSheetPreview {
  readonly index: number;
  readonly name: string;
  readonly headers: readonly string[];
  readonly samples: readonly Readonly<Record<string, string>>[];
  readonly rowCount: number;
  readonly error: string | null;
}

export interface DatasetWorkbookPreview {
  readonly sheets: readonly DatasetSheetPreview[];
}

type DatasetFile = { readonly name: string; arrayBuffer(): Promise<ArrayBuffer> };

function csvCells(line: string, delimiter: ',' | ';'): string[] {
  const cells: string[] = [];
  let current = '';
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]!;
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else quoted = !quoted;
    } else if (char === delimiter && !quoted) {
      cells.push(current.trim());
      current = '';
    } else current += char;
  }
  if (quoted) throw new Error('CSV با سلول چندخطی یا نقل‌قول ناقص پشتیبانی نمی‌شود.');
  cells.push(current.trim());
  return cells;
}

function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'object' && 'result' in value) return cellText(value.result);
  if (typeof value === 'object' && 'text' in value) return cellText(value.text);
  return '';
}

function validateSheetHeaders(headers: readonly string[]): void {
  if (!headers.length || headers.some((header) => !header))
    throw new Error('ردیف اول شیت باید عنوان داشته باشد؛ ستون بدون عنوان را نام‌گذاری کنید.');
  if (new Set(headers).size !== headers.length)
    throw new Error('عنوان ستون تکراری است؛ برای هر ستون عنوان یکتا بگذارید.');
}

function sample(headers: readonly string[], cells: readonly string[]): Record<string, string> {
  return Object.fromEntries(headers.map((header, index) => [header, cells[index] ?? '']));
}

/** Local transient preview only; callers must never persist or log sample contents. */
export async function readDatasetWorkbookPreview(
  file: DatasetFile,
): Promise<DatasetWorkbookPreview> {
  const bytes = await file.arrayBuffer();
  if (file.name.toLowerCase().endsWith('.csv')) {
    let text: string;
    try {
      text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
      throw new Error('فایل CSV باید با کدگذاری UTF-8 ذخیره شود.');
    }
    const lines = text
      .replace(/^\uFEFF/, '')
      .split(/\r?\n/)
      .filter((line) => line.trim() !== '');
    const firstLine = lines[0] ?? '';
    const comma = csvCells(firstLine, ',');
    const semicolon = csvCells(firstLine, ';');
    const delimiter = semicolon.length > comma.length ? ';' : ',';
    const headers = delimiter === ';' ? semicolon : comma;
    validateSheetHeaders(headers);
    const samples: Record<string, string>[] = [];
    let rowCount = 0;
    for (const line of lines.slice(1)) {
      const cells = csvCells(line, delimiter);
      if (cells.every((cell) => !cell)) continue;
      rowCount += 1;
      if (samples.length < 20) samples.push(sample(headers, cells));
    }
    return { sheets: [{ index: 0, name: 'CSV', headers, samples, rowCount, error: null }] };
  }
  if (!file.name.toLowerCase().endsWith('.xlsx'))
    throw new Error('فقط فایل CSV یا XLSX پذیرفته می‌شود.');
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(bytes);
  if (!workbook.worksheets.length) throw new Error('فایل Excel هیچ شیتی ندارد.');
  return {
    sheets: workbook.worksheets.map((sheet, index) => {
      const first = sheet.getRow(1);
      const headers = Array.from({ length: first.cellCount }, (_, column) =>
        cellText(first.getCell(column + 1).value).trim(),
      );
      while (headers.length && !headers[headers.length - 1]) headers.pop();
      let error: string | null = null;
      try {
        validateSheetHeaders(headers);
      } catch (cause) {
        error = cause instanceof Error ? cause.message : 'عنوان ستون‌های شیت نامعتبر است.';
      }
      const samples: Record<string, string>[] = [];
      let rowCount = 0;
      sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber === 1) return;
        const cells = Array.from({ length: Math.max(headers.length, row.cellCount) }, (_, column) =>
          cellText(row.getCell(column + 1).value).trim(),
        );
        if (cells.every((cell) => !cell)) return;
        rowCount += 1;
        if (!error && samples.length < 20) samples.push(sample(headers, cells));
      });
      return { index, name: sheet.name, headers, samples, rowCount, error };
    }),
  };
}
